import type { ReactNode } from "react";
import { StyleSheet, type TextStyle, View, type ViewProps } from "react-native";
import type { StyleProp } from "react-native/Libraries/StyleSheet/StyleSheet";
import { BodyText } from "~/components/layout/text";
import { Gap, Padding, Rounded } from "~/constants/layout";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";
import { QendoIcon, type QendoIconSet } from "../QendoIcon";

export type BadgeVariant = "info" | "danger" | "success" | "neutral" | "warning" | "primary" | "secondary" | "tertiary";
export type BadgeSize = "sm" | "md" | "lg";
export type BadgeRoundness = "none" | "full";

export type BadgeProps = {
    title: ReactNode;
    icon?: QendoIconSet;
    variant?: BadgeVariant;
    size?: BadgeSize;
    rounding?: BadgeRoundness;
    textStyle?: StyleProp<TextStyle>;
} & ViewProps;

type IconSizes = Record<BadgeSize, number>;

export const Badge = ({
    title,
    icon,
    variant = "info",
    size = "md",
    rounding,
    textStyle,
    ...viewProps
}: BadgeProps) => {
    const { colours, background } = useTheme();

    return (
        <View
            {...viewProps}
            style={[
                styles.container,
                background[variant === "neutral" ? "primary" : variant],
                paddingSizes[size],
                Rounded[rounding ?? size],
                rounding === "full" && circularPadding[size],
                viewProps.style,
            ]}
        >
            {filled(icon) && <QendoIcon name={icon} size={iconSize[size]} color={colours.icon[variant]} />}
            <BodyText colour={variant} size={size} weight={size === "md" ? "semibold" : "regular"} style={textStyle}>
                {title}
            </BodyText>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        flexShrink: 0,
    },
});

const paddingSizes = {
    sm: [Padding.vertical_xxx, Padding.horizontal_xx, Gap.xs],
    md: [Padding.vertical_xx, Padding.horizontal_xs, Gap.sm],
    lg: [Padding.vertical_sm, Padding.horizontal_sm, Gap.sm],
};
const circularPadding = {
    sm: [Padding.horizontal_xs],
    md: [Padding.horizontal_sm],
    lg: [Padding.horizontal_lg],
};

const iconSize: IconSizes = {
    sm: 16,
    md: 20,
    lg: 20,
};
